import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getPendingReviews, approveReview, deleteReview, getContentFlags, updateContentFlag } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { CheckCircle, Trash2, ShieldAlert, Flag, Loader2, Star } from 'lucide-react';

export default function AdminModeration(): React.JSX.Element {
  const toast = useToast();
  const [reviews, setReviews] = useState<any[]>([]);
  const [flags, setFlags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'reviews' | 'flags'>('reviews');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, flagsRes] = await Promise.all([
        getPendingReviews(),
        getContentFlags(),
      ]);
      setReviews(reviewsRes.reviews || []);
      setFlags(flagsRes.flags || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load moderation data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await approveReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.show('Review approved', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to approve', 'error');
    }
  };

  const handleDeleteReview = async (id: number) => {
    try {
      await deleteReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.show('Review deleted', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to delete', 'error');
    }
  };

  const handleFlag = async (id: number, status: 'reviewed' | 'dismissed') => {
    try {
      await updateContentFlag(id, { status });
      setFlags((prev) => prev.filter((f) => f.id !== id));
      toast.show(`Flag ${status}`, 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to update flag', 'error');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <ShieldAlert className="w-7 h-7 text-amber-600" />
          <h1 className="text-2xl font-bold text-slate-900">Content Moderation</h1>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              activeTab === 'reviews' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Pending Reviews ({reviews.length})
          </button>
          <button
            onClick={() => setActiveTab('flags')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              activeTab === 'flags' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Content Flags ({flags.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : activeTab === 'reviews' ? (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <Card className="text-center py-12 text-slate-500">No pending reviews.</Card>
            ) : (
              reviews.map((review) => (
                <Card key={review.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{review.name}</span>
                        <span className="text-xs text-slate-500">{review.email}</span>
                      </div>
                      <p className="text-xs text-amber-500 mb-2">{review.course_title}</p>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                          />
                        ))}
                      </div>
                      <p className="text-sm font-medium text-slate-900">{review.title}</p>
                      <p className="text-sm text-slate-600">{review.review}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" onClick={() => handleApprove(review.id)} leftIcon={<CheckCircle className="w-4 h-4" />}>
                        Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteReview(review.id)} leftIcon={<Trash2 className="w-4 h-4 text-red-500" />}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {flags.length === 0 ? (
              <Card className="text-center py-12 text-slate-500">No pending flags.</Card>
            ) : (
              flags.map((flag) => (
                <Card key={flag.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Flag className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-semibold text-slate-900 capitalize">{flag.content_type}</span>
                        <span className="text-xs text-slate-500">ID: {flag.content_id}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">Flagged by: {flag.flagged_by_name}</p>
                      <p className="text-sm text-slate-700">{flag.reason}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" onClick={() => handleFlag(flag.id, 'reviewed')} leftIcon={<CheckCircle className="w-4 h-4" />}>
                        Reviewed
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleFlag(flag.id, 'dismissed')}>
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
